/**
 * Project Edit / Overview — standalone page (NOT a tab).
 *
 * Route: `/projects/:projectId/edit`. Opened from the project
 * topbar's "Edit" button. Lives outside the tab strip so the
 * editing surface gets full vertical space, and so the user
 * has an unambiguous "I'm editing this project" mode separate
 * from the day-to-day tabs (Transactions, Party, Site, …).
 *
 * Layout:
 *   ┌─ Page header ────────────────────────────────────────┐
 *   │  ← Back to project   Project overview                │
 *   │                       Edit details · track progress  │
 *   └──────────────────────────────────────────────────────┘
 *   ┌─ Editable project details ───────────────────────────┐
 *   │  name · status · client · location · typology …      │
 *   │  start · end · contract value · MANUAL PROGRESS bar  │
 *   └──────────────────────────────────────────────────────┘
 *   ┌─ Progress (2-col) ───────────────────────────────────┐
 *   │  Task completion %        Budget utilisation %       │
 *   └──────────────────────────────────────────────────────┘
 *   ┌─ Danger zone ────────────────────────────────────────┐
 *   │  Delete project (cascade)                            │
 *   └──────────────────────────────────────────────────────┘
 *
 * The body is provided by the existing `OverviewTab` body
 * component — same form, same auto-save, same delete flow —
 * so the page is just a thin route + chrome wrapper.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';

import { canManageProjects } from '@shared/perms';

import { PagePlaceholder } from '@/components/PagePlaceholder';
import { useSetBreadcrumb } from '@/components/page/BreadcrumbContext';
import { useSetProjectHeader } from '@/components/chrome/ProjectHeaderContext';
import { db } from '@/lib/firebase';
import { useCurrentOrgRole } from '@/lib/useCurrentOrgRole';
import type { ProjectListItem } from '@/lib/useProjects';
import { OverviewTab } from '@/routes/projectDetail/OverviewTab';

export function ProjectEditPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  // Role gate — mirrors mobile `useGuardedRoute({ capability:
  // 'project.edit' })` at app/(app)/projects/[id]/edit-project.tsx:131.
  // SA / Admin / Manager can edit project metadata + the Delete
  // Project card (which still has its own per-action gate);
  // everyone else gets the placeholder.
  const { role: orgRole } = useCurrentOrgRole();
  const canEditProject = canManageProjects(orgRole);

  const [project, setProject] = useState<ProjectListItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Live subscription on the project doc — same pattern the
  // ProjectDetail tabbed page uses. Keeps the form in sync if
  // a teammate edits the project simultaneously on mobile.
  useEffect(() => {
    if (!projectId) return;
    setLoading(true);
    return onSnapshot(
      doc(db, 'projects', projectId),
      (snap) => {
        if (!snap.exists()) setProject(null);
        else setProject({ ...(snap.data() as ProjectListItem), id: snap.id });
        setLoading(false);
      },
      (err) => {
        setError(err.message);
        setLoading(false);
      },
    );
  }, [projectId]);

  // Same breadcrumb + topbar treatment as ProjectDetail. The
  // topbar's Share + Edit chips still render — clicking Edit
  // here is a no-op (already on /edit), but Share + Back stay
  // functional so users don't lose their way.
  useSetBreadcrumb(project?.name ?? null);
  useSetProjectHeader(project);

  if (loading) {
    return (
      <div className="page active" style={{ display: 'block' }}>
        <div className="card" style={{ padding: 32, textAlign: 'center' }}>
          Loading project…
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="page active" style={{ display: 'block' }}>
        <div
          className="card"
          style={{
            padding: 32,
            textAlign: 'center',
            color: 'var(--critical)',
          }}
        >
          Couldn't load project: {error}
        </div>
      </div>
    );
  }
  if (!project) {
    return (
      <div className="page active" style={{ display: 'block' }}>
        <div className="card" style={{ padding: 32, textAlign: 'center' }}>
          <div style={{ fontSize: 40, marginBottom: 12, opacity: 0.5 }}>
            <i className="ph ph-folder-simple-dashed" />
          </div>
          <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 6 }}>
            Project not found
          </div>
          <button
            className="btn-primary"
            type="button"
            onClick={() => navigate('/projects')}
          >
            <i className="ph ph-arrow-left" /> Back to Projects
          </button>
        </div>
      </div>
    );
  }

  // Role gate render — placed AFTER the loading/error/not-found
  // branches so the placeholder doesn't flash during the initial
  // project-doc load. orgRole resolves on first render; non-
  // edit-capable roles see the placeholder instead of the form.
  if (orgRole && !canEditProject) {
    return (
      <div
        className="page active"
        id="page-project-edit"
        style={{ display: 'block' }}
      >
        <PagePlaceholder
          title="Edit project"
          body="Only Super Admin, Admin, or Manager can edit project details. Ask one of them if changes are needed here."
        />
      </div>
    );
  }

  return (
    <div
      className="page active"
      id="page-project-edit"
      style={{ display: 'block' }}
    >
      {/* ─── Page header — back link + title + subtitle ─── */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          marginBottom: 20,
          paddingBottom: 16,
          borderBottom: '1px solid var(--border)',
        }}
      >
        <button
          type="button"
          className="btn-secondary"
          onClick={() => navigate(`/projects/${project.id}`)}
          title="Back to project"
          style={{
            padding: '6px 10px',
            fontSize: 12,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <i className="ph ph-arrow-left" /> Back to project
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h1
            style={{
              margin: 0,
              fontSize: 20,
              fontWeight: 700,
              color: 'var(--text-primary)',
              lineHeight: 1.2,
            }}
          >
            Project overview
          </h1>
          <div
            style={{
              fontSize: 12,
              color: 'var(--text-secondary)',
              marginTop: 4,
            }}
          >
            Edit details · update progress · delete project
          </div>
        </div>
      </div>

      {/* ─── Body — reuses the existing OverviewTab component
          (project details form + 2-col progress KPIs +
          danger-zone delete). Single source of truth so this
          page and the tab variant can never drift. ─── */}
      <OverviewTab project={project} />
    </div>
  );
}
